import { router } from "@inertiajs/react";
import type Participation from "@travily/types/models/MessagesParticipation";
import { useCallback } from "react";

export const CONVERSATION_KINDS = ["all", "communities", "families", "people"] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

/** Individual and Personal are two account shapes for the same thing on this surface: one person to
 *  talk to, rather than a household or a group. */
const KIND_PROFILE_TYPES: Record<Exclude<ConversationKind, "all">, ReadonlyArray<string>> = {
  communities: ["Community"],
  families: ["Family"],
  people: ["Individual", "Personal"],
};

const KIND_PARAM = "kind";
const UNREAD_PARAM = "unread";

function isConversationKind(value: string | null): value is ConversationKind {
  return value !== null && (CONVERSATION_KINDS as ReadonlyArray<string>).includes(value);
}

function splitUrl(url: string): { path: string; params: URLSearchParams } {
  const [path = url, query = ""] = url.split("?");
  return { path, params: new URLSearchParams(query) };
}

/**
 * List filters, derived from the page URL rather than held in component state: every mounted panel
 * reads the same source of truth, and drilling into a conversation and coming back restores them
 * from the URL Inertia kept in history.
 *
 * Writes go through `router.replace`, not a server visit: the whole conversation list is already on
 * the client, so filtering is a local concern and a round trip would only buy a flash of the
 * unfiltered list. Unlike raw `history.replaceState`, `router.replace` also rewrites the URL inside
 * Inertia's page and history state, so back-navigation and `router.reload` keep it.
 *
 * `url` is passed in rather than read from `usePage` here, because this drives a view — and a view
 * has to render outside Inertia, which is what its stories do.
 */
export function useConversationFilters(url: string) {
  const { params } = splitUrl(url);
  const kindParam = params.get(KIND_PARAM);
  const kind: ConversationKind = isConversationKind(kindParam) ? kindParam : "all";
  const unreadOnly = params.get(UNREAD_PARAM) === "1";

  const write = useCallback(
    (next: { kind: ConversationKind; unreadOnly: boolean }) => {
      const { path, params: nextParams } = splitUrl(url);
      if (next.kind === "all") nextParams.delete(KIND_PARAM);
      else nextParams.set(KIND_PARAM, next.kind);
      if (next.unreadOnly) nextParams.set(UNREAD_PARAM, "1");
      else nextParams.delete(UNREAD_PARAM);
      const query = nextParams.toString();
      router.replace({
        preserveScroll: true,
        preserveState: true,
        url: `${path}${query ? `?${query}` : ""}`,
      });
    },
    [url],
  );

  const setKind = useCallback((next: ConversationKind) => write({ kind: next, unreadOnly }), [unreadOnly, write]);
  const setUnreadOnly = useCallback((next: boolean) => write({ kind, unreadOnly: next }), [kind, write]);

  const apply = useCallback(
    (participations: Participation[]) =>
      participations.filter((participation) => {
        if (unreadOnly && !participation.unread) return false;
        if (kind === "all") return true;
        return KIND_PROFILE_TYPES[kind].includes(participation.other_account.profile_type);
      }),
    [kind, unreadOnly],
  );

  return { apply, isFiltering: kind !== "all" || unreadOnly, kind, setKind, setUnreadOnly, unreadOnly };
}
